import React from 'react';

import { makeStyles } from "@material-ui/core/styles";

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';

import DateFnsUtils from '@date-io/date-fns';
import {
    MuiPickersUtilsProvider,
    KeyboardDatePicker
} from '@material-ui/pickers';

import { getTimeZone, getPickerTimes } from '../common';


const useStyles = makeStyles(theme => ({
    form: {
        display: 'flex',
        '& .MuiButton-containedPrimary': {
            width: 100,
            maxWidth: '100%',
        },
    },
    repeatNumber: {
        width: 63,
        '& .MuiOutlinedInput-input': {
            paddingTop: 8,
            paddingBottom: 8,
            marginTop: 5,
        },
    },
}));

const AvailabilityForm = (props) => {
    const {
        dates,
        handleSaveButtonClick,
    } = props;

    const classes = useStyles();

    const isEdit = dates !== undefined;

    const timeZone = isEdit ? getTimeZone(dates.start) : getTimeZone(0);

    const parseTime = time => {
        const str = new Date(time).toTimeString().substring(0,5);
        if (str.substr(0,1) == '0') {
            return str.substr(1);
        } else {
            return str;
        }
    }

    const clearedForm = {
        start: dates ? new Date(dates.start * 1000) : new Date(new Date().setHours(12,0,0)),
        start_time: dates ? parseTime(dates.start * 1000) : '12:00',
        end: dates ?  new Date(dates.end * 1000) : new Date(new Date().setHours(12,0,0)),
        end_time: dates ? parseTime(dates.end * 1000) : '12:00',
        entire_day: false,
        repeat: false,
        repeat_number: 1,
        note: dates ? dates.note : ''
    }

    const [state, setState] = React.useState(clearedForm);
    const [showProgress, setShowProgress] = React.useState(false);

    const getUpdatedDateTime = (original, value) => {
        const t = value.split(':');
        return original.setHours(t[0], t[1], 0);
    }

    const updateState = (value, name) => {
        const data = {
            ...state,
            [name]: value,
        };
        if (name == 'start_time') {
            data['start'] = new Date(getUpdatedDateTime(data['start'], value));
        } else if (name == 'end_time') {
            data['end'] = new Date(getUpdatedDateTime(data['end'], value));
        }
        if (name == 'start' && data['end'] < value) {
            data['end'] = new Date(getUpdatedDateTime(data['start'], data['end_time']));
        }
        setState(data);
    }

    const handleChange = name => event => {
        let value;
        if (name == 'entire_day' || name == 'repeat') {
            value = event.target.checked;
        } else if (name == 'start' || name == 'end') {
            value = event;
        } else {
            value = event.target.value;
        }
        updateState(value, name);
    }

    const submitForm = e => {
        e.preventDefault();
        const formData = {
            ...state,
            _token: window._token
        };
        new Promise((resolve, reject) => {
            const url = isEdit ? `/api/unavailability/${dates.id}` : '/api/unavailability';
            setShowProgress(true);
            resolve();
            fetch(url, {
                method: 'POST',
                body: JSON.stringify(formData),
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    handleSaveButtonClick();
                })
                .catch(reason => {
                    alert('Please try again');
                })
                .finally(() => {
                    setShowProgress(false);
                })
        });
    }

    const timeOptions = getPickerTimes();

    return (
        <form className={classes.form} onSubmit={submitForm}>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                            id="date-start"
                            label="Date From"
                            format="MM/dd/yyyy"
                            fullWidth
                            value={state.start}
                            onChange={handleChange('start')}
                            KeyboardButtonProps={{
                                'aria-label': 'change date',
                            }}
                        />
                    </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={3}>
                    <TextField
                        id="start-time"
                        label={`Time (${timeZone.initials})`}
                        select
                        fullWidth
                        disabled={state.entire_day}
                        value={state.start_time}
                        onChange={handleChange('start_time')}
                        SelectProps={{
                            autoWidth: true
                        }}
                    >
                        {timeOptions.map(option => (
                            <MenuItem key={`starttime-${option.id}`} value={option.value}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={3}>
                    <FormGroup style={{marginTop: 10}}>
                        <FormControlLabel
                            control={
                                <Checkbox color="primary" id="entire-day" checked={state.entire_day} onChange={handleChange('entire_day')} value={state.entire_day} />
                            }
                            label="Entire Day"
                        />
                    </FormGroup>
                </Grid>

                <Grid item xs={6}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                            id="date-end"
                            label="Date To"
                            format="MM/dd/yyyy"
                            fullWidth
                            disabled={state.entire_day}
                            value={state.end}
                            onChange={handleChange('end')}
                            KeyboardButtonProps={{
                                'aria-label': 'change date',
                            }}
                        />
                    </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={3}>
                    <TextField
                        id="end-time"
                        label={`Time (${timeZone.initials})`}
                        select
                        fullWidth
                        disabled={state.entire_day}
                        value={state.end_time}
                        onChange={handleChange('end_time')}
                        SelectProps={{
                            autoWidth: true
                        }}
                    >
                        {timeOptions.map(option => (
                            <MenuItem key={`endtime-${option.id}`} value={option.value}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {!isEdit && <Grid item xs={12} style={{marginTop: 4, marginBottom: 4}}>
                    <FormGroup>
                        <Grid container>
                            <Grid item>
                                <FormControlLabel
                                    control={
                                        <Checkbox color="primary" id="repeat" checked={state.repeat} onChange={handleChange('repeat')} value={state.repeat} />
                                    }
                                    label="Repeat for"
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    id="repeat-number"
                                    type="number"
                                    value={state.repeat_number}
                                    className={classes.repeatNumber}
                                    variant="outlined"
                                    onChange={handleChange('repeat_number')}
                                />
                            </Grid>
                            <Grid item style={{paddingLeft: 10, paddingTop: 8}}>
                                <Typography>additional week(s)</Typography>
                            </Grid>
                        </Grid>
                    </FormGroup>
                </Grid>}

                <Grid item xs={12} style={{marginTop: 4, marginBottom: 4}}>
                    <TextField
                        id="note"
                        label="Note"
                        value={state.note || ''}
                        fullWidth
                        onChange={handleChange('note')}
                    />
                </Grid>

                <Grid item xs={12} style={{marginTop: 4, marginBottom: 4}}>
                    <Box component="span">
                        <Button
                            type="submit"
                            disabled={!showProgress ? false : true}
                            variant="contained"
                            color="primary"
                            endIcon={showProgress ? <CircularProgress size={20} /> : null}
                        >
                            Save
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </form>
    )
}

export default AvailabilityForm;
